import {
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
  forwardRef,
  useId,
} from 'react';
import type { CountryCode } from 'libphonenumber-js';

import { cn } from '@/lib/utils';
import { FieldShell, type FieldStatus } from '@/components/form-elements/Field';
import {
  PHONE_COUNTRIES,
  getCountryName,
  getDialCode,
  getPhonePlaceholder,
} from '@/utils/helpers/phone.helpers';

/**
 * Worldwide mobile number field (GIFTWEB-17), lifted from the OTC prototype:
 * one bordered control holding a dial-code chip ("+44 ⌄") with an invisible
 * native <select> laid over it — so the country picker is keyboard navigable
 * and type-ahead searchable for free — a hairline divider and the number input,
 * whose placeholder is the selected country's example mobile number.
 */
export type PhoneFieldProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'id' | 'className' | 'placeholder' | 'type'
> & {
  id?: string;
  label: ReactNode;
  /** Helper shown between the label and the control. */
  hint?: ReactNode;
  country: CountryCode;
  onCountryChange: (country: CountryCode) => void;
  countrySelectProps?: {
    name?: string;
    onBlur?: React.FocusEventHandler<HTMLSelectElement>;
    ref?: React.Ref<HTMLSelectElement>;
  };
  error?: string;
  nudge?: boolean;
  className?: string;
};

const controlClasses = (status: FieldStatus) =>
  cn(
    'flex h-[54px] w-full items-center gap-[11px] rounded-[13px] border-[1.5px] border-[#8b8a88] bg-white px-[15px]',
    'transition-[border-color,box-shadow] duration-150',
    'focus-within:border-[#256956] focus-within:ring-[3px] focus-within:ring-[#256956]/20',
    status === 'nudge' &&
      'border-[#66bda4] ring-4 ring-[#66bda4]/25 animate-[req-pulse_.5s_ease]',
    status === 'error' && 'border-[#f43f5e] bg-[#fff1f2]',
  );

export const PhoneField = forwardRef<HTMLInputElement, PhoneFieldProps>(
  function PhoneField(
    {
      id,
      label,
      hint,
      country,
      onCountryChange,
      countrySelectProps,
      error,
      nudge,
      required,
      className,
      ...inputProps
    },
    ref,
  ) {
    const autoId = useId();
    const fieldId = id ?? autoId;
    const selectId = `${fieldId}-country`;
    const status: FieldStatus = error ? 'error' : nudge ? 'nudge' : 'default';

    return (
      <FieldShell
        id={fieldId}
        label={label}
        required={required}
        hintAbove={hint}
        error={error}
        nudge={nudge}
        className={className}
      >
        {(a11y) => (
          <div className={controlClasses(status)} data-testid={`${fieldId}-control`}>
            <span className='relative flex shrink-0 items-center gap-[5px]'>
              <span
                className='text-base font-semibold text-[#1a201d]'
                data-testid={`${fieldId}-dial-code`}
                aria-hidden='true'
              >
                {getDialCode(country)}
              </span>
              <svg
                width='13'
                height='13'
                viewBox='0 0 24 24'
                fill='none'
                stroke='#6b736e'
                strokeWidth='2.4'
                strokeLinecap='round'
                strokeLinejoin='round'
                aria-hidden='true'
              >
                <path d='M6 9l6 6 6-6' />
              </svg>
              <select
                id={selectId}
                aria-label='Country dialling code'
                title={getCountryName(country)}
                value={country}
                name={countrySelectProps?.name}
                onBlur={countrySelectProps?.onBlur}
                ref={countrySelectProps?.ref}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  onCountryChange(e.target.value as CountryCode)
                }
                className='absolute -inset-y-4 -inset-x-2 w-[calc(100%+16px)] cursor-pointer opacity-0'
              >
                {PHONE_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name} ({c.dialCode})
                  </option>
                ))}
              </select>
            </span>
            <span
              aria-hidden='true'
              className='h-[22px] w-px shrink-0 bg-[#e0ddd5]'
            />
            <input
              ref={ref}
              id={fieldId}
              type='tel'
              inputMode='tel'
              autoComplete='tel-national'
              placeholder={getPhonePlaceholder(country)}
              className='min-w-0 flex-1 border-none bg-transparent p-0 text-base text-[#1a201d] outline-none placeholder:text-[#6b736e]'
              {...a11y}
              {...inputProps}
            />
          </div>
        )}
      </FieldShell>
    );
  },
);

export default PhoneField;
